// src/pages/Platform/Tabs/Chatbot/ChatbotWidget/ChatHeader.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

// Minimal SVG icons
const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

const DownloadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const WandIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 4V2" />
    <path d="M15 16v-2" />
    <path d="M8 9h2" />
    <path d="M20 9h2" />
    <path d="M17.8 11.8L19 13" />
    <path d="M15 9h.01" />
    <path d="M17.8 6.2L19 5" />
    <path d="m3 21 9-9" />
    <path d="M12.2 6.2L11 5" />
  </svg>
);

const SuggestionsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
    <line x1="9" y1="10" x2="15" y2="10"></line>
  </svg>
);

const ChatHeader = ({
  headerColor,
  headerStyle,
  botIconUrl,
  botIconOffsetX = 0,
  botIconOffsetY = 0,
  assistantName,
  botTagline,
  isPlatform,
  onToggleChat,
  onReportIssue,
  onClearChat,
  messages,
  onNotification,
  humanHandoffConfig,
  onTalkToHuman,
  // Suggestions toggle
  suggestionsEnabled,
  onSuggestionsToggle
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Toggle menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Close menu
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const hexToRGB = (hex) => {
    if (!hex) return '0, 0, 0';
    hex = hex.replace('#', '');
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  };

  // Calculate text color based on header background for optimal contrast
  const getContrastColor = (hexColor) => {
    if (!hexColor) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? '#1a1a1a' : '#ffffff';
  };

  const headerTextColor = getContrastColor(headerColor);

  // Download transcript function
  const handleDownloadTranscript = () => {
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    const timeStr = now.toLocaleTimeString();

    let transcript = `Chat Transcript with ${assistantName}\n`;
    transcript += `Generated on: ${dateStr} at ${timeStr}\n\n`;

    messages.forEach((msg, index) => {
      const msgTime = new Date(now - (messages.length - index) * 60000);
      const timeString = msgTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const role = msg.role === 'user' ? 'You' : assistantName;
      transcript += `[${timeString}] ${role}: ${msg.content}\n\n`;
    });

    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-transcript-${dateStr.replace(/\//g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    if (onNotification) onNotification('Transcript downloaded!');
    setIsMenuOpen(false);
  };

  // Clear chat function
  const handleClearChat = () => {
    if (onClearChat) {
      onClearChat();
      if (onNotification) onNotification('Chat cleared!');
    }
    setIsMenuOpen(false);
  };

  return (
    <div
      className={`chatbot-header ${headerStyle}`}
      style={{
        '--header-color': headerColor,
        '--header-color-rgb': hexToRGB(headerColor),
        '--header-text-color': headerTextColor,
      }}
    >
      {headerStyle === 'modern' && <div className="blur-layer"></div>}
      <div className="header-content">
        {botIconUrl && (
          <img
            src={botIconUrl}
            alt="Bot Avatar"
            className="bot-avatar"
            style={{
              objectPosition: `calc(50% + ${botIconOffsetX}px) calc(50% + ${botIconOffsetY}px)`
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        )}
        <div className="bot-info">
          <span className="bot-name">{assistantName}</span>
          <span className="bot-tagline">{botTagline}</span>
        </div>
      </div>

      {/* Header Actions */}
      <div className="header-actions">
        {/* More Menu Button */}
        <div className="header-menu-container">
          <button
            className="header-icon-btn"
            onClick={toggleMenu}
            aria-label="More options"
            title="More options"
          >
            <MoreIcon />
          </button>
        </div>

        {/* Dropdown Menu with backdrop */}
        {isMenuOpen && (
          <>
            {/* Invisible backdrop to close menu on click outside */}
            <div
              className="dropdown-backdrop"
              onClick={closeMenu}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 9998
              }}
            />
            <div className="header-dropdown-menu">
              <button className="dropdown-item" onClick={handleDownloadTranscript}>
                <DownloadIcon />
                <span>Download Transcript</span>
              </button>
              {humanHandoffConfig?.enabled && humanHandoffConfig?.allowUserInitiated && (
                <button className="dropdown-item" onClick={() => { onTalkToHuman(); closeMenu(); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
                  </svg>
                  <span>Talk to Human</span>
                </button>
              )}
              {onReportIssue && (
                <button className="dropdown-item" onClick={() => { onReportIssue(); closeMenu(); }}>
                  <WandIcon />
                  <span>Report Issue on Page</span>
                </button>
              )}
              {onSuggestionsToggle && (
                <button
                  className="dropdown-item"
                  onClick={() => { onSuggestionsToggle(); closeMenu(); }}
                >
                  <SuggestionsIcon />
                  <span>{suggestionsEnabled ? 'Disable Suggestions' : 'Enable Suggestions'}</span>
                </button>
              )}
              <button className="dropdown-item danger" onClick={handleClearChat}>
                <TrashIcon />
                <span>Clear Chat</span>
              </button>
            </div>
          </>
        )}

        {/* Close Button */}
        {onToggleChat && (
          <button
            id="chatbox-close-button"
            className="header-icon-btn close-btn"
            onClick={onToggleChat}
            aria-label="Close Chatbot"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
    </div>
  );
};

export default ChatHeader;